import { t } from "i18next";

import type { AddFavouriteRecipientRequest } from "@thalamos/aspire-common";

import { api } from "../api.js";
import { logErrorToDatadog } from "../tracing.js";
import { renderErrorToast } from "~/components/design-system/index.js";

/**
 * Add a favourite recipient for the current user.
 * Shows an error toast on failure and logs unexpected failures to Datadog.
 * @returns true if the recipient is now a favourite, false otherwise
 */
export async function addFavouriteRecipient(
  favourite: AddFavouriteRecipientRequest,
  refetchUser: () => void,
): Promise<boolean> {
  try {
    const response = await api.users.addFavouriteRecipient(favourite);
    // The route answers an add beyond the organisation's favourites limit with
    // a 400 that states its `reason`: a refusal the user can act on, not a
    // fault. Any other 400 is a fault, reported below.
    const body: unknown = response.data;
    const reachedFavouritesLimit =
      response.status === 400 &&
      typeof body === "object" &&
      body !== null &&
      "reason" in body;
    if (reachedFavouritesLimit) {
      renderErrorToast({
        message: t("pages.formDraftSignPage.favourites.maxFavouritesReached"),
      });
      return false;
    }
    // A stale favourites list makes the client re-add an existing favourite;
    // the server's 409 means the user already has what they asked for.
    const alreadyFavourite = response.status === 409;
    if (response.status !== 204 && !alreadyFavourite) {
      const error = new Error(
        `Unexpected status code: ${String(response.status)}`,
      );
      logErrorToDatadog(error, {
        action: "addFavouriteRecipient",
        favourite,
      });
      renderErrorToast({
        message: "Failed to add favourite recipient",
      });
      return false;
    }
    refetchUser();
    return true;
  } catch (error) {
    logErrorToDatadog(
      error instanceof Error ? error : new Error(String(error)),
      { action: "addFavouriteRecipient", favourite },
    );
    renderErrorToast({ message: "Failed to add favourite recipient" });
    return false;
  }
}

/**
 * Remove a favourite recipient for the current user.
 * Shows error toast and logs to Datadog on failure.
 * @returns true if the recipient is no longer a favourite, false otherwise
 */
export async function removeFavouriteRecipient(
  dataControllerId: string,
  teamId: string,
  userId: string | undefined,
  refetchUser: () => void,
): Promise<boolean> {
  try {
    const response = await api.users.removeFavouriteRecipient(
      dataControllerId,
      teamId,
      userId,
    );
    // A repeated request or another tab can remove the favourite first; the
    // server's 404 means the user already has what they asked for.
    const alreadyRemoved = response.status === 404;
    if (response.status !== 204 && !alreadyRemoved) {
      const error = new Error(
        `Unexpected status code: ${String(response.status)}`,
      );
      logErrorToDatadog(error, {
        action: "removeFavouriteRecipient",
        dataControllerId,
        teamId,
        userId,
      });
      renderErrorToast({
        message: "Failed to remove favourite recipient",
      });
      return false;
    }
    refetchUser();
    return true;
  } catch (error) {
    logErrorToDatadog(
      error instanceof Error ? error : new Error(String(error)),
      { action: "removeFavouriteRecipient", dataControllerId, teamId, userId },
    );
    renderErrorToast({
      message: "Failed to remove favourite recipient",
    });
    return false;
  }
}
